import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import { createDirectDevice } from '@/features/operations/api';
import {
  CatalogueModelFields,
  ColorField,
} from '@/features/operations/components/CatalogueModelFields';
import {
  conditionQuestions,
  conditionAnswers,
  type ConditionDraft,
} from '@/features/operations/condition';
import { priceError } from '@/features/operations/money';
import type {
  BranchOption,
  CreateDirectDeviceInput,
  DeviceDetail,
  DeviceModelOption,
  InspectionFieldSummary,
  PublicDeclaredField,
} from '@/features/operations/types';

const fixed = ['Estado de la pantalla', 'Estado de la carcasa', 'Salud de la batería'];

export function DirectDeviceForm({
  open,
  token,
  models,
  branches,
  fields,
  onClose,
  onCreated,
}: {
  open: boolean;
  token: string;
  models: DeviceModelOption[];
  branches: BranchOption[];
  fields: PublicDeclaredField[];
  onClose: () => void;
  onCreated: (device: DeviceDetail) => void;
}) {
  const configured = useMemo(() => fields.filter((field) => fixed.includes(field.label)), [fields]);
  const inspectionFields: InspectionFieldSummary[] = configured.map((field) => ({
    fieldId: field.id,
    fieldLabel: field.label,
    fieldType: field.type,
    options: field.options.map((option) => ({ ...option, isActive: true })),
  }));
  const choiceFields = conditionQuestions(inspectionFields);
  const batteryField = configured.find((field) => field.label === fixed[2]);
  const [modelId, setModelId] = useState('');
  const [capacity, setCapacity] = useState('');
  const [colorId, setColorId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [modality, setModality] = useState('');
  const [battery, setBattery] = useState('');
  const [serial, setSerial] = useState('');
  const [paidPrice, setPaidPrice] = useState('');
  const [note, setNote] = useState('');
  const [condition, setCondition] = useState<ConditionDraft>({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const reset = () => {
    setModelId('');
    setCapacity('');
    setColorId('');
    setBranchId('');
    setModality('');
    setBattery('');
    setSerial('');
    setPaidPrice('');
    setNote('');
    setCondition({});
    setError('');
  };
  const submit = async () => {
    if (
      !modelId ||
      !capacity ||
      !colorId ||
      !branchId ||
      !modality ||
      !battery ||
      !batteryField ||
      choiceFields.some((field) => !condition[field.fieldId]) ||
      !note.trim()
    ) {
      setError(
        'Completá el catálogo, las tres características, la sucursal, la modalidad y la nota de ingreso.',
      );
      return;
    }
    const priceProblem = paidPrice.trim() && priceError(paidPrice, 'El precio pagado');
    if (priceProblem) {
      setError(priceProblem);
      return;
    }
    const input: CreateDirectDeviceInput = {
      phoneModelId: modelId,
      storageGb: Number(capacity),
      colorId,
      branchId,
      commercialModality: modality as CreateDirectDeviceInput['commercialModality'],
      batteryHealth: Number(battery),
      serialNumber: serial.trim() || undefined,
      paidPrice: paidPrice.trim() || undefined,
      note: note.trim(),
      answers: conditionAnswers(inspectionFields, condition),
    };
    setSaving(true);
    setError('');
    try {
      const device = await createDirectDevice(token, input);
      reset();
      onCreated(device);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No se pudo ingresar el equipo.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="md">
      <DialogTitle>Ingresar equipo</DialogTitle>
      <DialogContent>
        <Grid container spacing={1.5} sx={{ mt: 0.2 }}>
          <CatalogueModelFields
            options={models}
            phoneModelId={modelId}
            onChange={(id) => {
              setModelId(id);
              setCapacity('');
              setColorId('');
            }}
          />
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Capacidad (GB)"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              disabled={!modelId}
            >
              <MenuItem value="">Elegí una capacidad</MenuItem>
              {(models.find((m) => m.id === modelId)?.storageOptions ?? []).map((n) => (
                <MenuItem key={n} value={n}>
                  {n} GB
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ColorField
              options={models}
              phoneModelId={modelId}
              colorId={colorId}
              onChange={setColorId}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Sucursal"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              <MenuItem value="">Elegí una sucursal</MenuItem>
              {branches
                .filter((b) => b.isActive)
                .map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name} · {b.city}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Modalidad comercial"
              value={modality}
              onChange={(e) => setModality(e.target.value)}
            >
              <MenuItem value="">Elegí una modalidad</MenuItem>
              <MenuItem value="DIRECT_PURCHASE">Compra directa</MenuItem>
              <MenuItem value="TRADE_IN">Parte de pago</MenuItem>
              <MenuItem value="CONSIGNMENT">Consignación</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Número de serie"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Batería (%)"
              inputProps={{ min: 0, max: 100 }}
              value={battery}
              onChange={(e) => setBattery(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Precio pagado (USD) · opcional"
              value={paidPrice}
              onChange={(e) => setPaidPrice(e.target.value)}
            />
          </Grid>
          {choiceFields.map((field) => (
            <Grid item xs={12} sm={6} key={field.fieldId}>
              <TextField
                required
                select
                fullWidth
                size="small"
                label={field.fieldLabel}
                value={condition[field.fieldId] ?? ''}
                onChange={(e) =>
                  setCondition((current) => ({ ...current, [field.fieldId]: e.target.value }))
                }
              >
                <MenuItem value="">Elegí una opción</MenuItem>
                {field.options.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ))}
          {!batteryField ? (
            <Grid item xs={12}>
              <Alert severity="warning">
                Falta configurar “Salud de la batería” en las características.
              </Alert>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              multiline
              minRows={2}
              label="Nota de ingreso"
              helperText="Explicá por qué se ingresa sin cotización. Quedará en el historial con tu usuario y fecha."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Grid>
          {error ? (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="contained" disabled={saving} onClick={() => void submit()}>
          {saving ? 'Guardando…' : 'Ingresar equipo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
